import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FornecedorService } from 'src/app/service/fornecedor.service';
import { ViaCepService } from 'src/app/service/viaCep.service';
import { Utils } from 'src/app/util/utils';
import { CidadeService } from 'src/app/service/cidade.service';
import { EstadosBrasil, NomesEstadosBrasil } from 'src/app/enum/estadosBrasil.enum';
import { Fornecedor } from './fornecedor';

@Component({
  selector: 'app-fornecedor-form',
  templateUrl: './fornecedor-form.component.html',
  styleUrls: ['./fornecedor.component.scss'],
})
export class FornecedorFormComponent implements OnInit {

  formulario: FormGroup;
  public errorMessage: string = '';
  estados = Object.values(EstadosBrasil);
  cidades: String[] = [];
  carregando = false;

  constructor(
    private fornecedorService: FornecedorService,
    private formBilder: FormBuilder,
    private snackBar: MatSnackBar,
    private viaCepService: ViaCepService,
    private utils: Utils,
    private cidadeService: CidadeService,
    private route: ActivatedRoute,
    private router: Router,
  ) { }

  ngOnInit(): void {
    this.montarFormulario();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.carregar(+id);
    }
  }

  carregar(id: number) {
    this.carregando = true;
    this.fornecedorService.findFornecedorById(id).subscribe((response) => {
      this.formulario.controls.id.setValue(id);
      this.formulario.controls.razaoSocial.setValue(response.razaoSocial);
      this.formulario.controls.nomeFantasia.setValue(response.nomeFantasia);
      this.formulario.controls.address.setValue(response.address);
      this.formulario.controls.block.setValue(response.block);
      this.formulario.controls.batch.setValue(response.batch);
      this.formulario.controls.telephone.setValue(response.telephone);
      this.formulario.controls.complement.setValue(response.complement);
      this.formulario.controls.observation.setValue(response.observation);
      this.formulario.controls.cidade.setValue(response.cidade);
      this.formulario.controls.estado.setValue(response.estado);
      this.formulario.controls.bairro.setValue(response.bairro);
      this.formulario.controls.cep.setValue(response.cep);
      if (response.estado) {
        this.buscarMunicipios(this.buscarSiglaEstadoInformado(response.estado));
      }
      this.carregando = false;
    }, () => {
      this.carregando = false;
      this.snackBar.open('Fornecedor não encontrado.', 'ERRO', { duration: 2500 });
      this.voltarConsulta();
    });
  }

  submit() {
    const formValues = this.formulario.value;
    const fornecedor: Fornecedor = new Fornecedor(
      formValues.id,
      formValues.nomeFantasia,
      formValues.razaoSocial,
      formValues.address,
      formValues.block,
      formValues.batch,
      formValues.telephone,
      formValues.complement,
      formValues.observation,
      formValues.bairro,
      formValues.cidade,
      formValues.estado,
      formValues.cep
    );

    const request = formValues.id
      ? this.fornecedorService.update(fornecedor)
      : this.fornecedorService.save(fornecedor);

    request.subscribe(() => {
      this.snackBar.open(formValues.id ? 'Fornecedor alterado com sucesso!' : 'Fornecedor salvo com sucesso!', 'Sucesso', {
        duration: 2000
      });
      this.voltarConsulta();
    }, errorResponse => {
      this.snackBar.open(errorResponse, 'ERRO', { duration: 2000 });
    });
  }

  montarFormulario() {
    this.formulario = this.formBilder.group({
      id: [null, Validators.nullValidator],
      nomeFantasia: [null, [Validators.minLength(3), Validators.maxLength(250)]],
      razaoSocial: [null, Validators.nullValidator],
      telephone: [null, Validators.nullValidator],
      address: [null, Validators.nullValidator],
      block: [null, Validators.nullValidator],
      batch: [null, Validators.nullValidator],
      complement: [null],
      observation: [null],
      estado: [null, Validators.nullValidator],
      bairro: [null, Validators.nullValidator],
      cidade: [null],
      cep: [null],
    });
  }

  limparFormulario() {
    this.router.navigate(['/fornecedor/form']);
    this.formulario.reset();
    this.cidades = [];
  }

  voltarConsulta() {
    this.router.navigate(['/fornecedor/consulta']);
  }

  onEstadoChange(estado: EstadosBrasil) {
    this.buscarMunicipios(this.getSiglaEstado(estado));
    this.formulario.controls.cidade.setValue(null);
  }

  buscarDadosCep(): void {
    this.mostrarLoading();
    if (!this.utils.isNullOrEmpty(this.formulario.get('cep')?.value)) {
      this.viaCepService.buscarCep(this.formulario.get('cep')?.value).subscribe(resposta => {
        this.formulario.controls['estado']!.setValue(resposta.estado);
        this.formulario.controls['cidade']!.setValue(resposta.localidade);
        this.formulario.controls['bairro']!.setValue(resposta.bairro);
        this.formulario.controls['address']!.setValue(resposta.logradouro);
        this.buscarMunicipios(this.buscarSiglaEstadoInformado(resposta.estado));
        this.fecharLoading();
      }, () => {
        this.formulario.controls['estado']!.setValue('');
        this.formulario.controls['cidade']!.setValue('');
        this.formulario.controls['bairro']!.setValue('');
        this.formulario.controls['address']!.setValue('');
        this.cidades = [];
        this.fecharLoading();
      });
    } else {
      this.formulario.controls['estado']!.setValue('');
      this.formulario.controls['cidade']!.setValue('');
      this.formulario.controls['bairro']!.setValue('');
      this.formulario.controls['address']!.setValue('');
      this.cidades = [];
      this.fecharLoading();
    }
  }

  buscarMunicipios(uf: string): void {
    if (!this.utils.isNullOrEmpty(uf)) {
      this.cidadeService.buscarMunicipios(uf).subscribe(resposta => {
        this.cidades = resposta;
      });
    } else {
      this.cidades = [];
    }
  }

  getSiglaEstado(estado: EstadosBrasil): string {
    return NomesEstadosBrasil.get(estado) ?? '';
  }

  buscarSiglaEstadoInformado(estado: string): string {
    if (!this.utils.isNullOrEmpty(estado)) {
      const sigla = Object.entries(EstadosBrasil).find(
        ([, nome]) => nome.toLowerCase() === estado.toLowerCase()
      )?.[0];
      return sigla || '';
    }
    return '';
  }

  public mostrarLoading() {
    document.getElementById('preloader')?.setAttribute('style', 'display:block;');
  }

  public fecharLoading() {
    document.getElementById('preloader')?.setAttribute('style', 'display:none;');
  }
}
